import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useForm, FieldError, FormProvider, Controller, Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BULK_UPLOAD_CERTS_KEY, BULK_UPLOAD_PICTURES_KEY, useDraftBulkUploadDocuments } from '@steelbuy/data-access';
import { FormControl, MaterialSelection, NumberInput, Select, TextInput } from '@steelbuy/form';
import { Modal, ModalAccessor } from '@steelbuy/modal-dialog';
import {
    AddressModel,
    DimensionUnit,
    DocumentType,
    findCurrencySymbolByCode,
    getCoatingThicknessUnit,
    getCurrencyFromRegion,
    getDimensionUnit,
    getPricingUnit,
    getRegion,
    getTolerances,
    getTradeUnit,
    Grade,
    isStackableProduct,
    isTheoreticalWeight,
    ListingMaterialAge,
    Literal,
    MaterialProperties,
    Product,
    Quality,
    UploadFile,
} from '@steelbuy/ts-shared';
import { getRules, useLettersOnlyValidator, useNumberValidator, useRequiredFieldValidator } from '@steelbuy/ui-domain';
import {
    ButtonDanger,
    ButtonPrimary,
    ButtonTertiary,
    FileUploadStatus,
    FormItem,
    Icon,
    IconIdentifier,
    InputFile,
    InputRadioButton,
    InputRadioButtonGroup,
    InputRadioButtonGroupAlign,
    InputTextAreaCharacterCount,
    LoadingStatus,
    ModalContent,
    ModalContentActionBar,
    Notification,
    NotificationLevel,
    Separator,
    SeparatorIdentifier,
    ToastContainerId,
    toaster,
} from '@steelbuy/ui-primitive';
import { getThicknessTypes } from '@steelbuy/util';
import { useIsVendorFeatureSupported } from '@steelbuy/vendor';
import { BulkListingDescriptionInput } from './BulkListingDescriptionInput';
import { BulkListingPreviousFiles } from './BulkListingPreviousFiles';
import { useFileInputStatus } from '../../../hooks/useFileInputStatus';
import { FormLayout } from '../../../views/layouts/form-layout/FormLayout';
import { Steps } from '../../create-listing/CreateListingContext.enums';
import CollectionAddresses from '../../create-listing/steps/CollectionAddresses';
import {
    BulkListing,
    mapFields,
    removeNullFields,
    FormData,
    CertificateOption,
    getTheoreticalWeightForListing,
    getListingInputs,
} from '../bulkListingUtil';

import './EditListing.scss';
import './EditListingModal.scss';

const modalSelector = '.modal__wrapper';

type MaterialData = MaterialProperties & {
    listingId: string;
    testCertificate?: UploadFile;
    length?: number;
    price?: number;
    collectionAddressNotes?: string;
    pickupAddress?: AddressModel;
    materialId: string;
    age: ListingMaterialAge;
    width: number;
    certificateToFollow: boolean;
    prime: boolean;
    picture?: UploadFile;
    quality?: Quality;
};

interface EditModalProps {
    modalAccessor: ModalAccessor;
    index: number;
    materialData: MaterialData;
    update: (listingId: string, updatedData: BulkListing) => void;
    onNext?: () => void;
    totalCount: number;
    onDelete?: () => void;
    isLastListing: boolean;
    bulkUploadFormData: FieldValues;
    addEditedListing: (listingId: string) => void;
}

const NUMBER_VALIDATION_FIELDS = [
    'width',
    'thickness',
    'weight',
    'price',
    'numberOfItems',
    'coatingThicknessValue',
] as const;

const LETTER_ONLY_VALIDATION_FIELDS = ['coatingColour'] as const;

type OtherSupportedType = Pick<FormData, 'coatingColour' | 'coatingThicknessValue'>;

const OTHER_SUPPORTED_SELECTION: Partial<keyof OtherSupportedType>[] = ['coatingColour', 'coatingThicknessValue'];

const OTHER_FIELDS: Record<keyof OtherSupportedType, keyof FormData> = {
    coatingThicknessValue: 'coatingThickness',
    coatingColour: 'coatingColourValue',
};

const CUSTOM_FIELDS = [
    'testCertificateOption',
    'previousCertificateId',
    'uploadedTestCertificateId',
    'previousPictureId',
    'uploadedPictureId',
    'coatingThickness',
    'coatingColourValue',
] as const;

const OTHER = 'OTHER';

const getDefaultTestCertificateOption = (materialData: MaterialData) => {
    if (materialData.certificateToFollow) {
        return CertificateOption.TO_FOLLOW;
    }
    if (materialData.testCertificate || materialData.picture) {
        return CertificateOption.PREVIOUS;
    }
    return undefined;
};

export const SUCCESSFUL_TOAST_ID = 'bulk_listing_saved';

export const EditModalForm = ({
    modalAccessor,
    index,
    materialData,
    update,
    onNext,
    totalCount,
    onDelete,
    isLastListing,
    bulkUploadFormData,
    addEditedListing,
}: EditModalProps) => {
    const { t } = useTranslation(['translation', 'domainModel', 'uiDomain']);
    const rules = getRules(t);
    const requiredFieldValidator = useRequiredFieldValidator();
    const numberValidator = useNumberValidator();
    const letterOnlyValidator = useLettersOnlyValidator();
    const { draftId = '' } = useParams();
    const queryClient = useQueryClient();
    const [isInitialValidationComplete, setIsInitialValidationComplete] = useState(false);
    const isThicknessTypeSupported = useIsVendorFeatureSupported('THICKNESS_TYPE');
    const showServiceFeeNotification = useIsVendorFeatureSupported('SHOW_SERVICE_FEE_NOTIFICATION');
    const isQualityCategoriesEnabled = useIsVendorFeatureSupported('NON_PRIME_CATEGORIES');

    const methods = useForm<FormData>({
        mode: 'all',
        defaultValues: {
            ...mapFields(materialData, 'mapStateForInput'),
            testCertificateOption: getDefaultTestCertificateOption(materialData),
            previousCertificateId: materialData?.testCertificate?.documentId,
            previousPictureId: materialData?.picture?.documentId,
            uploadedTestCertificateId: undefined,
            uploadedPictureId: undefined,
        },
    });

    const {
        control,
        getValues,
        formState: { errors, isValid, dirtyFields },
        watch,
        clearErrors,
        setValue,
        trigger,
    } = methods;

    const certFile = useRef<File | null>(null);
    const pictureFile = useRef<File | null>(null);

    const {
        testCertificateOption,
        previousCertificateId,
        previousPictureId,
        uploadedTestCertificateId,
        uploadedPictureId,
        testCertificate,
        certificateToFollow,
        quality,
    } = watch();

    const requiresCertificate = isQualityCategoriesEnabled ? quality === Quality.PRIME : materialData.prime;
    const hasCertificate = certificateToFollow || testCertificate?.documentId;

    const isFormValid = isValid && (!requiresCertificate || hasCertificate);
    const {
        certificatesQuery: {
            data: prevTestCertificates,
            fetchNextPage: fetchNextPageCerts,
            isFetchingNextPage: isFetchingNextPageCerts,
            total: totalPrevTestCerts,
            hasNextPage: hasNextPageCerts,
            trim: trimCertificates,
        },
        picturesQuery: {
            data: prevPictures,
            fetchNextPage: fetchNextPagePictures,
            isFetchingNextPage: isFetchingNextPagePictures,
            total: totalPrevPictures,
            hasNextPage: hasNextPagePictures,
            trim: trimPictures,
        },
    } = useDraftBulkUploadDocuments({
        bulkId: draftId,
        queryOptions: { enabled: false },
    });

    const setDocumentId = (documentId: string | undefined, field: 'testCertificate' | 'picture') => {
        setValue(field, documentId ? ({ documentId } as UploadFile) : undefined, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const setTestCertificateId = (documentId: string | undefined) => setDocumentId(documentId, 'testCertificate');
    const setPictureId = (documentId: string | undefined) => setDocumentId(documentId, 'picture');

    const {
        validFileTypes: validCertFileTypes,
        onFileUpload: onCertUpload,
        reset: resetCertFileStatus,
        fileStatus: certFileStatus,
        getInvalidMessage: getInvalidMessageCert,
    } = useFileInputStatus({
        type: DocumentType.ORIGINAL,
        onSuccess: (data) => {
            setValue('uploadedTestCertificateId', data.id);
            setTestCertificateId(data.id);
        },
        onError: () => {
            setValue('uploadedTestCertificateId', undefined);
            setTestCertificateId(undefined);
            certFile.current = null;
        },
        bulkId: draftId,
    });

    const {
        validFileTypes: validPictureFileTypes,
        onFileUpload: onPictureUpload,
        reset: resetPictureFileStatus,
        fileStatus: pictureFileStatus,
        getInvalidMessage: getInvalidMessagePicture,
    } = useFileInputStatus({
        type: DocumentType.PICTURE,
        onSuccess: (data) => {
            setValue('uploadedPictureId', data.id);
            setPictureId(data.id);
        },
        onError: () => {
            setValue('uploadedPictureId', undefined);
            setPictureId(undefined);
            pictureFile.current = null;
        },
        bulkId: draftId,
    });

    const areFilesUploading = Object.values({ ...certFileStatus, ...pictureFileStatus }).includes(
        FileUploadStatus.Uploading
    );

    useEffect(() => {
        trigger();
        setTimeout(() => {
            setIsInitialValidationComplete(true);
        }, 0);
    }, []);

    const reset = () => {
        clearErrors();
        resetCertFileStatus();
        resetPictureFileStatus();
        certFile.current = null;
        pictureFile.current = null;
    };

    const trimDocuments = () => {
        trimCertificates();
        trimPictures();
    };

    const onClose = () => {
        trimDocuments();
        reset();
        toast.dismiss(SUCCESSFUL_TOAST_ID);
        modalAccessor.hide();
    };

    const handleSave = (goToNext = false) => {
        const formData = getValues();
        if (formData) {
            const formValidations: Partial<BulkListing> = {};
            OTHER_SUPPORTED_SELECTION.forEach((field) => {
                const value = formData[field];
                const tempField = OTHER_FIELDS[field];
                const tempValue = formData[tempField];
                if (value === OTHER) {
                    if (tempValue) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        formData[field] = tempValue as any;
                    } else {
                        formData[field] = '';
                    }
                }
            });
            NUMBER_VALIDATION_FIELDS.forEach((field) => {
                if (formData[field] && !numberValidator.validate(formData[field] as number | string)) {
                    formValidations[field as keyof BulkListing] = undefined;
                }
            });
            LETTER_ONLY_VALIDATION_FIELDS.forEach((field) => {
                if (formData[field] && !letterOnlyValidator.validate(formData[field] as number | string)) {
                    formValidations[field as keyof BulkListing] = undefined;
                }
            });
            CUSTOM_FIELDS.forEach((field) => {
                delete formData[field];
            });

            const updatedFormData = removeNullFields({
                ...formData,
                ...formValidations,
            });

            trimDocuments();

            if (certFile.current) {
                queryClient.invalidateQueries([BULK_UPLOAD_CERTS_KEY]);
            }

            if (pictureFile.current) {
                queryClient.invalidateQueries([BULK_UPLOAD_PICTURES_KEY]);
            }

            if (Object.keys(dirtyFields).length) {
                addEditedListing(materialData.listingId);
                update(materialData.listingId, mapFields(updatedFormData, 'mapInput'));
            }

            let isActive = false;
            if (toast.isActive(SUCCESSFUL_TOAST_ID)) {
                isActive = true;
                toast.dismiss(SUCCESSFUL_TOAST_ID);
            }

            const showSuccessToast = () => {
                toaster.success(t('application.createBulkListing.editListingModal.successNotification'), {
                    containerId: ToastContainerId.MODAL,
                    toastId: SUCCESSFUL_TOAST_ID,
                });
            };

            if (goToNext) {
                reset();
                if (isActive) {
                    setTimeout(showSuccessToast, 10);
                } else {
                    showSuccessToast();
                }
                onNext?.();
                document.querySelector(modalSelector)?.scrollTo?.(0, 0);
            } else {
                onClose();
                toaster.success(t('application.createBulkListing.editListingModal.successNotification'), {
                    toastId: SUCCESSFUL_TOAST_ID,
                });
            }
        }
    };
    const isPrime = isQualityCategoriesEnabled ? quality === Quality.PRIME : bulkUploadFormData?.prime === true;

    const formData = getValues();

    const hasLength =
        bulkUploadFormData?.product === Product.PLATE ||
        bulkUploadFormData?.product === Product.SHEET ||
        bulkUploadFormData?.product === Product.TREAD ||
        bulkUploadFormData?.product === Product.FLOOR;

    const descriptionInputProps = useMemo(
        () => ({
            isPrime,
            control,
            validationRules: rules,
        }),
        [isPrime, control, rules]
    );

    const isWeightTheoretical = isTheoreticalWeight(materialData);
    const tradeUnit = t(`domainModel:material.tradeUnit.value.${getTradeUnit(materialData)}.label`);
    const theoreticalWeight = getTheoreticalWeightForListing({ ...materialData, ...getValues() });

    const thicknessRules = {
        ...rules.required(),
        ...(getDimensionUnit() === DimensionUnit.MILLIMETER && rules.min(0.1000000001, 0.1)),
    };

    const qualityOptions = [Quality.PRIME, Quality.EXCESS, Quality.SECONDARY].map((qualityValue) => ({
        label: t(`domainModel:material.quality.value.${qualityValue}`),
        value: qualityValue,
    }));
    const grade = watch('grade');

    return (
        <FormProvider {...methods}>
            <div className="edit-listing-modal">
                <ModalContent
                    onCloseModal={onClose}
                    headline={t('application.createBulkListing.editListingModal.header', {
                        index: index + 1,
                        total: totalCount,
                    })}
                >
                    <div style={{ visibility: isInitialValidationComplete ? 'visible' : 'hidden' }}>
                        <div className="edit-listing-modal-content">
                            {!isFormValid && (
                                <Notification
                                    className="edit-listing-modal__incomplete-notification"
                                    level={NotificationLevel.ERROR}
                                    message={t('application.createBulkListing.editListingModal.incompleteNotification')}
                                    disableScroll
                                    key={`bulk-edit-error-notification-${index}`}
                                />
                            )}

                            <div className="edit-listing-modal__inputs">
                                <FormControl
                                    label={t(`application.createBulkListing.editListingModal.materialIdLabel`)}
                                    isMandatory
                                    className="form-control-material-id"
                                >
                                    <TextInput
                                        control={control}
                                        name="materialId"
                                        rules={rules.required()}
                                        error={(errors?.materialId as FieldError)?.message}
                                        defaultValue={materialData.materialId || ''}
                                    />
                                </FormControl>
                                {isQualityCategoriesEnabled && (
                                    <FormControl label={t(`domainModel:material.quality.name`)} isMandatory>
                                        <Select
                                            options={qualityOptions}
                                            placeholder={t(`uiDomain:materialInput.placeholder`)}
                                            name="quality"
                                            control={control}
                                            rules={rules.required()}
                                            error={(errors?.quality as FieldError)?.message}
                                        />
                                    </FormControl>
                                )}
                                {getListingInputs().map((input) => {
                                    let label;
                                    let isRequired = true;
                                    switch (input.field) {
                                        case 'age':
                                            label = t(`domainModel:material.age.nameShort`);
                                            isRequired = false;
                                            break;
                                        case 'coatingThicknessValue':
                                            label = t(`domainModel:material.coatingThickness.name` as Literal);
                                            label += ` (${getCoatingThicknessUnit(getValues())})`;
                                            break;
                                        case 'gradeFamily':
                                            label = t(`domainModel:material.${input.field}.name` as Literal);
                                            isRequired = grade !== Grade.NO_GRADE;
                                            break;
                                        case 'compliance':
                                            label = t(`domainModel:material.${input.field}.name` as Literal);
                                            isRequired = grade !== Grade.NO_GRADE;
                                            break;
                                        default:
                                            label = t(`domainModel:material.${input.field}.name` as Literal);
                                            break;
                                    }
                                    const Component = input.Component ?? MaterialSelection;
                                    return (
                                        <Component
                                            key={input.field}
                                            material={watch()}
                                            control={control}
                                            name={input.field}
                                            getOptions={(materialProperties: MaterialProperties) =>
                                                input.getOptions(
                                                    materialProperties,
                                                    (isQualityCategoriesEnabled
                                                        ? quality === Quality.PRIME
                                                        : formData.prime) ?? false
                                                )
                                            }
                                            rules={isRequired ? rules.required() : undefined}
                                            error={errors?.[input.field] as FieldError}
                                            label={label}
                                            sort={input.sort}
                                            isRequired={isRequired}
                                        />
                                    );
                                })}
                            </div>
                            <div className="input_test-certificate">
                                <FormItem header={t('uiDomain:materialTestCert.header')} isMandatory>
                                    {!isPrime && <BulkListingDescriptionInput {...descriptionInputProps} />}
                                    <Controller
                                        control={control}
                                        name="testCertificateOption"
                                        rules={isPrime ? rules.required() : {}}
                                        render={({ field: { value, onChange } }) => (
                                            <InputRadioButtonGroup align={InputRadioButtonGroupAlign.ALIGN_HORIZONTAL}>
                                                <InputRadioButton
                                                    value={t(
                                                        'application.createBulkListing.editListingModal.previouslyUploadedLabel'
                                                    )}
                                                    onChange={(checked) => {
                                                        if (checked) {
                                                            onChange(CertificateOption.PREVIOUS);
                                                            setTestCertificateId(previousCertificateId || undefined);
                                                            setPictureId(previousPictureId || undefined);
                                                            setValue('certificateToFollow', false, {
                                                                shouldValidate: true,
                                                            });
                                                        }
                                                    }}
                                                    checked={value === CertificateOption.PREVIOUS}
                                                    disabled={!prevTestCertificates?.length && !prevPictures?.length}
                                                />
                                                <InputRadioButton
                                                    value={t(
                                                        'application.createBulkListing.editListingModal.uploadNewFile'
                                                    )}
                                                    onChange={(checked) => {
                                                        if (checked) {
                                                            onChange(CertificateOption.NEW);
                                                            setTestCertificateId(
                                                                uploadedTestCertificateId || undefined
                                                            );
                                                            setPictureId(uploadedPictureId || undefined);
                                                            setValue('certificateToFollow', false, {
                                                                shouldValidate: true,
                                                            });
                                                        }
                                                    }}
                                                    checked={value === CertificateOption.NEW}
                                                />
                                                {isPrime && (
                                                    <InputRadioButton
                                                        value={t('uiDomain:materialTestCert.testCertToFollow')}
                                                        onChange={(checked) => {
                                                            if (checked) {
                                                                onChange(CertificateOption.TO_FOLLOW);
                                                                setTestCertificateId(undefined);
                                                                setValue('certificateToFollow', true, {
                                                                    shouldValidate: true,
                                                                });
                                                            }
                                                        }}
                                                        checked={value === CertificateOption.TO_FOLLOW}
                                                    />
                                                )}
                                            </InputRadioButtonGroup>
                                        )}
                                    />
                                </FormItem>
                                {testCertificateOption === CertificateOption.PREVIOUS ? (
                                    <div className="input-previous-files">
                                        {previousCertificateId === undefined && requiresCertificate && (
                                            <Notification
                                                level={NotificationLevel.ERROR}
                                                message={t(
                                                    'application.createBulkListing.editListingModal.selectTestCertNotification'
                                                )}
                                                disableScroll
                                            />
                                        )}
                                        <BulkListingPreviousFiles
                                            files={prevPictures}
                                            onChange={(documentId: string) => {
                                                setValue('previousPictureId', documentId);
                                                setPictureId(documentId);
                                            }}
                                            selectedDocumentId={previousPictureId}
                                            totalFiles={totalPrevPictures}
                                            fetchNextPage={fetchNextPagePictures}
                                            isFetchingNextPage={isFetchingNextPagePictures}
                                            hasNextPage={hasNextPagePictures}
                                            label={t('uiDomain:materialQualitySelection.pictureUploadLabel')}
                                        />
                                        <BulkListingPreviousFiles
                                            files={prevTestCertificates}
                                            onChange={(documentId: string) => {
                                                setValue('previousCertificateId', documentId);
                                                setTestCertificateId(documentId);
                                            }}
                                            selectedDocumentId={previousCertificateId}
                                            totalFiles={totalPrevTestCerts}
                                            fetchNextPage={fetchNextPageCerts}
                                            isFetchingNextPage={isFetchingNextPageCerts}
                                            hasNextPage={hasNextPageCerts}
                                            label={
                                                isPrime
                                                    ? undefined
                                                    : t(
                                                          'uiDomain:materialQualitySelection.testCertificateLabelOptional'
                                                      )
                                            }
                                        />
                                    </div>
                                ) : null}
                                {testCertificateOption === CertificateOption.NEW && (
                                    <div className="input-fileupload">
                                        {!isPrime && (
                                            <div className="input-fileupload__picture">
                                                <InputFile
                                                    label={t('uiDomain:materialQualitySelection.pictureUploadLabel')}
                                                    fileType={validPictureFileTypes}
                                                    name="picture"
                                                    onChange={(files) => {
                                                        const isPictureValid = onPictureUpload(files);
                                                        pictureFile.current =
                                                            isPictureValid && files?.[0] ? files?.[0] : null;
                                                    }}
                                                    fileStatus={pictureFileStatus}
                                                    onRemoveFile={() => {
                                                        resetPictureFileStatus();
                                                        setPictureId(undefined);
                                                        pictureFile.current = null;
                                                    }}
                                                    initialSelectedFiles={
                                                        pictureFile.current ? [pictureFile.current] : undefined
                                                    }
                                                    getInvalidMessage={getInvalidMessagePicture}
                                                />
                                            </div>
                                        )}
                                        <InputFile
                                            label={
                                                isPrime
                                                    ? t('uiDomain:materialQualitySelection.testCertificateLabel')
                                                    : t(
                                                          'uiDomain:materialQualitySelection.testCertificateLabelOptional'
                                                      )
                                            }
                                            fileType={validCertFileTypes}
                                            name="testCertificate"
                                            onChange={(files) => {
                                                const isCertValid = onCertUpload(files);
                                                certFile.current = isCertValid && files?.[0] ? files?.[0] : null;
                                            }}
                                            fileStatus={certFileStatus}
                                            onRemoveFile={() => {
                                                resetCertFileStatus();
                                                setTestCertificateId(undefined);
                                                certFile.current = null;
                                            }}
                                            validators={isPrime ? [requiredFieldValidator] : undefined}
                                            initialSelectedFiles={certFile.current ? [certFile.current] : undefined}
                                            isMandatory={isPrime}
                                            forceValidation
                                            getInvalidMessage={getInvalidMessageCert}
                                        />
                                    </div>
                                )}
                                {isPrime && <BulkListingDescriptionInput {...descriptionInputProps} />}
                            </div>
                            <div className="input-dimensions">
                                <FormItem header={t(`application.createListingSteps.labels.${Steps.DIMENSIONS}`)}>
                                    <div className="input-dimensions__inputs">
                                        <FormControl
                                            label={t('application.createBulkListing.fields.thickness', {
                                                unit: t(
                                                    `domainModel:material.dimensions.units.${getDimensionUnit()}.label`
                                                ),
                                            })}
                                            isMandatory
                                        >
                                            <NumberInput
                                                control={control}
                                                name="thickness"
                                                rules={thicknessRules}
                                                error={(errors?.thickness as FieldError)?.message}
                                            />
                                        </FormControl>
                                        {isThicknessTypeSupported && (
                                            <MaterialSelection
                                                key="thicknessType"
                                                material={watch()}
                                                control={control as unknown as Control<FieldValues>}
                                                name="thicknessType"
                                                rules={rules.required()}
                                                getOptions={() => getThicknessTypes()}
                                                label={t('domainModel:material.thicknessType.name')}
                                                error={errors?.thicknessType as FieldError}
                                            />
                                        )}
                                        <FormControl
                                            label={t('application.createBulkListing.fields.width', {
                                                unit: t(
                                                    `domainModel:material.dimensions.units.${getDimensionUnit()}.label`
                                                ),
                                            })}
                                            isMandatory
                                        >
                                            <NumberInput
                                                control={control}
                                                name="width"
                                                rules={{
                                                    ...rules.required(),
                                                    ...rules.min(0.0000000001, 0),
                                                }}
                                                error={(errors?.width as FieldError)?.message}
                                            />
                                        </FormControl>
                                        {hasLength && (
                                            <FormControl
                                                label={t('application.createBulkListing.fields.length', {
                                                    unit: t(
                                                        `domainModel:material.dimensions.units.${getDimensionUnit()}.label`
                                                    ),
                                                })}
                                                isMandatory
                                            >
                                                <NumberInput
                                                    control={control}
                                                    name="length"
                                                    rules={{
                                                        ...rules.required(),
                                                        ...rules.min(0.0000000001, 0),
                                                    }}
                                                    error={(errors?.length as FieldError)?.message}
                                                />
                                            </FormControl>
                                        )}
                                        <MaterialSelection
                                            key="tolerance"
                                            material={watch()}
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            control={control as any}
                                            name="tolerance"
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            getOptions={getTolerances as any}
                                            rules={rules.required()}
                                            error={errors?.tolerance as FieldError}
                                            label={t(`domainModel:material.tolerance.name`)}
                                        />
                                    </div>
                                </FormItem>
                            </div>
                            <div className="input-weight-price">
                                <FormItem header={t('application.createListingSummary.weightAndPriceHeader')}>
                                    <div className="input-weight-price__inputs">
                                        {isWeightTheoretical ? (
                                            <FormControl
                                                label={t(
                                                    'application.createBulkListing.editListingModal.theoreticalWeight',
                                                    {
                                                        tradeUnit,
                                                    }
                                                )}
                                                isMandatory
                                            >
                                                <div className="input-weight-price__inputs__theoretical-weight">
                                                    {theoreticalWeight && !Number.isNaN(theoreticalWeight)
                                                        ? theoreticalWeight
                                                        : ''}
                                                </div>
                                            </FormControl>
                                        ) : (
                                            <FormControl
                                                label={t(
                                                    'application.createBulkListing.editListingModal.inputWeightLabel',
                                                    {
                                                        tradeUnit,
                                                    }
                                                )}
                                                isMandatory
                                            >
                                                <NumberInput
                                                    control={control}
                                                    name="weight"
                                                    rules={{
                                                        ...rules.required(),
                                                        ...rules.min(0.1000000001, 0.1),
                                                    }}
                                                    error={(errors?.weight as FieldError)?.message}
                                                />
                                            </FormControl>
                                        )}
                                        {isStackableProduct(formData.product) && (
                                            <FormControl
                                                label={t(`uiDomain:numberOfItemsLabel.label.${formData.product}`)}
                                                isMandatory
                                                className="form-control-price"
                                            >
                                                <NumberInput
                                                    control={control}
                                                    name="numberOfItems"
                                                    rules={{
                                                        ...rules.required(),
                                                        ...rules.min(1, 1),
                                                    }}
                                                    error={(errors?.numberOfItems as FieldError)?.message}
                                                    integerOnly
                                                />
                                            </FormControl>
                                        )}
                                        <FormControl
                                            label={t('application.createBulkListing.editListings.sellingPrice', {
                                                pricingUnit: t(
                                                    `domainModel:material.pricingUnit.value.${getPricingUnit(
                                                        materialData
                                                    )}.label`
                                                ),
                                            })}
                                            isMandatory
                                            className="form-control-price"
                                        >
                                            <NumberInput
                                                control={control}
                                                name="price"
                                                rules={rules.required()}
                                                error={(errors?.price as FieldError)?.message}
                                                preInput={findCurrencySymbolByCode(getCurrencyFromRegion(getRegion()))}
                                            />
                                        </FormControl>
                                    </div>
                                    {showServiceFeeNotification && (
                                        <div className="input_weight-notification">
                                            <Notification
                                                level={NotificationLevel.INFO}
                                                message={t(
                                                    'application.sellerListingDetails.weightAndSellingPrice.offerNotification'
                                                )}
                                            />
                                        </div>
                                    )}
                                </FormItem>
                            </div>
                            <div className="input__pickup-address">
                                <FormLayout>
                                    <div className="pickup-address-form">
                                        <Controller
                                            name="pickupAddress"
                                            control={control}
                                            rules={rules.required()}
                                            render={({ field: { value, onChange } }) => (
                                                <CollectionAddresses
                                                    forceValidation
                                                    isMandatory
                                                    showNotification={false}
                                                    pickupAddressId={value?.id}
                                                    selectedPickupAddress={(address) => onChange(address)}
                                                />
                                            )}
                                        />
                                    </div>
                                    <section className="collection-address-notes">
                                        <Controller
                                            name="collectionAddressNotes"
                                            control={control}
                                            render={({ field: { value, onChange } }) => (
                                                <InputTextAreaCharacterCount
                                                    label={t(
                                                        'translation:application.pickupAddressStep.additionalInformationLabel'
                                                    )}
                                                    onChange={(description) => {
                                                        onChange(description);
                                                    }}
                                                    value={value}
                                                    placeholder={t(
                                                        'translation:application.pickupAddressStep.additionalInfoPlaceholder'
                                                    )}
                                                />
                                            )}
                                        />
                                    </section>
                                </FormLayout>
                            </div>
                        </div>
                        <Separator separatorType={SeparatorIdentifier.ON_LIGHT} />
                        <div>
                            <ModalContentActionBar showSeparator={false}>
                                <ButtonDanger
                                    label={t('application.createBulkListing.editListingModal.deleteListingLabel')}
                                    icon={IconIdentifier.BIN}
                                    onClick={onDelete}
                                />
                                <ButtonTertiary label={t('uiDomain:common.cancel')} onClick={onClose} />
                                {isLastListing ? (
                                    <ButtonPrimary
                                        label={t('uiDomain:common.save')}
                                        onClick={() => handleSave()}
                                        loadingStatus={areFilesUploading ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                                    />
                                ) : (
                                    <ButtonPrimary
                                        label={t('application.createBulkListing.editListingModal.saveAndNext')}
                                        data-testid="save-btn"
                                        onClick={() => handleSave(true)}
                                        loadingStatus={areFilesUploading ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                                    />
                                )}
                            </ModalContentActionBar>
                            {!isLastListing && (
                                <div className="action-bar-helper">
                                    <Icon name={IconIdentifier.WORKFLOW_INCOMPLETE} />
                                    <span>{t('application.createBulkListing.editListingModal.saveAndNextHelper')}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </ModalContent>
            </div>
        </FormProvider>
    );
};

export const EditModal = (props: EditModalProps) => {
    const { materialData, modalAccessor } = props;
    const [rerenderKey, setRerenderKey] = useState(1);

    useEffect(() => {
        setRerenderKey((prev) => prev + 1);
    }, [materialData]);
    return (
        <Modal modalAccessor={modalAccessor} className="edit-listing-modal-container">
            <EditModalForm {...props} key={rerenderKey} />
        </Modal>
    );
};
